// Packages
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import debounce from 'lodash/debounce';
import loadable from '@loadable/component';

// Services
import disableElementService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/disableElementService';
import { apiFetchData, apiFetchDataWithoutLangISO } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError, reportErrorSSR } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { retryLoadableImport } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/retryService';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';
import searchResultsWindowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/searchResultsWindowManipulationService';
import screenService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/screenService';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import IterableTrackingService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/iterableTrackingService';
import SearchResultObserver from '@PROJECT_ROOT/assets/responsive-frontend/js/services/observer';
import { getDesiredPositionInsideFrameFromTopSoModalCanBeInsideViewport, IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import {
    dispatchTrackingEvent,
    hasGivenMarketingConsent,
    buildBaseAnalyticsItem,
    SEARCH_EVENT,
    SEARCH_RESULTS_FILTERS_MODAL,
    SEARCH_RESULTS_FILTERS_SUBMIT,
    SEARCH_RESULTS_LOAD_MORE,
    COULD_HAVE_DATES_FROM_LOCAL_STORAGE,
    SEARCH_RESULTS_VIEW_EXTRA_OFFER_BANNER,
    SEARCH_RESULTS_EXTRA_OFFER_BANNER,
    SEARCH_RESULTS_EXTRA_LOCATIONS,
    SEARCH_RESULTS_VIEW_EXTRA_LOCATIONS,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import {
    GUEST_SELECT_SEARCH_DOCK_ID,
    GUEST_SELECT_SEARCH_FORM_ID,
    DATES_SELECT_SEARCH_DOCK_ID,
    DATES_SELECT_SEARCH_FORM_ID,
    ADRIATIC_LAST_SEARCH_PARAMS,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/constants';
import { isVariantB } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/variantService';
// Između ostaloga pod komentarom zbog ovoga bug-a #57702
// import LocalStoreSearchParameters from '@PROJECT_ROOT/assets/responsive-frontend/js/services/LocalStoreSearchParameters';
// Components
import SearchResultsCard from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/searchResultsCard';
import runAfterNextPaint from '@PROJECT_ROOT/assets/responsive-frontend/js/services/runAfterNextPaint';
import BreadCrumbs from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/breadcrumbs';
import AxiosError from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/axiosError';
import SearchForm from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/searchForm';
import Loader from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/loader';
import Filters from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/filters';
import LoginForMoreBenefits from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/LoginForMoreBenefits';
import SearchResultsSingleUnitCard from '@PROJECT_ROOT/assets/responsive-frontend/parts-variants/SearchResultsCardVariantB/SearchResultsSingleUnitCard';
import Pagination from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/Pagination';

// Config
import pageConfig from '@PROJECT_ROOT/responsiveFrontendPagesConfiguration.json';

// Icons
import {
    MdOutlineTune,
    MdOutlineMap,
    MdOutlineFilterList,
    MdOutlineMenuOpen,
} from 'react-icons/md';
import { BiCollapseAlt } from 'react-icons/bi';

const FiltersModal = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendFiltersFromSearchResults" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/FiltersModal'
    ))
        .catch((err) => {
            // TODO: Prebacit u service ako se pokaze korisno.
            err.customMessage = 'RETRY IMPORT-a - error sa importa FiltersModal u SearchResultsContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);
const SearchMap = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendSearchMapFromSearchResults" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/SearchMap'
    ))
        .catch((err) => {
            // TODO: Prebacit u service ako se pokaze korisno.
            err.customMessage = 'RETRY IMPORT-a - error sa importa SearchMap u SearchResultsContent komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);

// Constants
const isClientSideRendering = windowManipulationService.isBrowserEnvironment();
export default class SearchResultsContent extends React.Component {
    static maxPriceUpperLimit = 2500;

    static filtersToReset = {
        prc_min: 0,
        prc_max: SearchResultsContent.maxPriceUpperLimit,
        t: '', // Vrsta smještaja - string
        dc_t: '', // Prikaži objekte samo na popustu - string
        bedrooms: 0, // Broj soba - int
        bathrooms: 0, // Broj zahoda - int
        kit_room: 0, // Kuhinja - boolean
        terrace: 0, // Balkon - boolean
        outer_area: 0, // Dvorište - boolean
        pool: 0, // bazen - boolean
        ac: 0, // Klima uređaj - boolean
        outer_grill: 0, // Vanjski roštilj - boolean
        private_pool: 0, // Privatni bazen - boolean
        jacuzzi: 0, // Jacuzzi - boolean
        tv: 0, // Televizijia - boolean
        outer_view_sea: 0, // Pogled na more - boolean
        pet: 0, // Dozvoljeni ljubimci - int
        wm: 0, // Perilica rublja - boolean
        moor: 0, // Vez za čamac - boolean
        parking: 0, // Parking - boolean
        food: 0, // Doručak - boolean
        inet: 0, // Internet - boolean
        fc: 0, // Besplatan otkaz - boolean
        category: '', // Kategorija smještaja - string
        pos: '', // Etaža - string
        be_d: 0, // Udaljenost od plaže - int
        ce_d: 0, // Udaljenost od centra - int
        be_t: '', // Vrsta plaže - string
        land: '', // Dostupnost lokacije - string
        f_score: '0', // Ocjena - string
        b_b: 0, // Best buy - int
    };

    static extractTotalResults(responseData) {
        if (responseData?.totalResults
            && typeof responseData.totalResults === 'string'
            && responseData.totalResults.includes(':')
        ) {
            const parsedResults = parseInt(responseData.totalResults.split(':')[1].trim(), 10);
            return !Number.isNaN(parsedResults) ? parsedResults : 0;
        }
        return 0;
    }

    static setInitialSortOption(responseData) {
        return responseData?.sort?.defaultOptionValues?.[0] ?? '';
    }

    static parseParamsHelper(parsedParams, selectedFiltersUpdates, selectedFiltersCount) {
        let updatedCount = selectedFiltersCount;
        let newValue;
        Object.entries(parsedParams).forEach(([key, value]) => {
            const currentValue = selectedFiltersUpdates?.key;
            switch (key) {
                case 'from':
                case 'to':
                case 'f_length':
                case 'i_d':
                case 'sort':
                    selectedFiltersUpdates[key] = value;
                    break;
                case 'dc_t':
                case 'be_t':
                    updatedCount += 1;
                    // if (currentValue !== value) {
                    selectedFiltersUpdates[key] = value;
                    // }
                    break;
                case 'locationId': {
                    const val = decodeURIComponent(value);
                    const outKey = 'location_id';
                    selectedFiltersUpdates[outKey] = val;
                }
                    break;
                case 'p_dist':
                case 'persons_adult':
                case 'persons_0_3':
                case 'persons_4_11':
                case 'persons_12_17':
                case 'page':
                    selectedFiltersUpdates[key] = parseInt(value, 10);
                    break;
                case 'be_d':
                case 'ce_d':
                case 'pet':
                case 'bedrooms':
                case 'bathrooms':
                case 'prc_min':
                    newValue = parseInt(value, 10);
                    updatedCount += 1;
                    // if (currentValue !== newValue) {
                    selectedFiltersUpdates[key] = newValue;
                    // }
                    break;
                case 'prc_max':
                    newValue = parseInt(value, 10);
                    updatedCount += 1;
                    // if (currentValue !== newValue) {
                    selectedFiltersUpdates[key] = newValue;
                    // }
                    break;
                case 'deviation':
                    if (value === '1') {
                        selectedFiltersUpdates[key] = true;
                    }
                    break;
                case 'kit_room':
                case 'terrace':
                case 'outer_area':
                case 'pool':
                case 'ac':
                case 'outer_grill':
                case 'private_pool':
                case 'jacuzzi':
                case 'tv':
                case 'outer_view_sea':
                case 'wm':
                case 'moor':
                case 'parking':
                case 'food':
                case 'inet':
                case 'fc':
                case 'b_b':
                    newValue = 1;
                    updatedCount += 1;
                    // if (currentValue !== newValue) {
                    selectedFiltersUpdates[key] = newValue;
                    // }
                    break;
                case 't':
                case 'category':
                case 'pos':
                case 'land':
                case 'f_score': {
                    newValue = value;
                    const items = Array.isArray(value) ? value : newValue.split('|');
                    const count = items.length;
                    const oldItems = currentValue ? currentValue.split('|') : [];
                    const oldCount = oldItems.length;
                    updatedCount += count - oldCount;
                    // if (currentValue !== newValue) {
                    selectedFiltersUpdates[key] = Array.isArray(newValue) ? newValue.join('|') : newValue;
                    // }
                    break;
                }
                default:
                    break;
            }
        });

        return { selectedFiltersUpdates, updatedCount };
    }

    static parseUrlParams(initialFilters) {
        const updatedCount = 0;
        const queryString = window.location.search.substr(1);
        const encodedQueryString = queryString.replace(/\+/g, '%2B');
        const searchURLParams = new URLSearchParams(encodedQueryString);

        return SearchResultsContent.parseParamsHelper(searchURLParams, initialFilters, updatedCount);
    }

    static parseQueryParams(selectedFiltersUpdates, selectedFiltersCount) {
        const dataParams = searchResultsWindowManipulationService.getParams();
        const parsedParams = JSON.parse(dataParams);
        if (parsedParams) {
            const { selectedFiltersUpdates: updatedFilters, updatedCount: selectedFiltersCountNew } = SearchResultsContent.parseParamsHelper(parsedParams, selectedFiltersUpdates, selectedFiltersCount);

            return { selectedFiltersUpdates: updatedFilters, updatedCount: selectedFiltersCountNew };
        }

        return { selectedFiltersUpdates, updatedCount: selectedFiltersCount };
    }

    static buildLinkPaginationSEO(url, page, originalParams) {
        try {
            if (!url || typeof url !== 'string') {
                throw new Error(`Neispravan URL (${url}) prilikom generiranja u buildLinkPaginationSEO`);
            }
            const pageNumber = Number(page);
            if (Number.isNaN(pageNumber) || pageNumber < 1) {
                throw new Error(`Neispravan PAGE (${pageNumber}) prilikom generiranja u buildLinkPaginationSEO`);
            }
            let paramsArray = [];
            if (originalParams && originalParams.trim()) {
                paramsArray = originalParams.split('&').map((originalParamItem) => {
                    const [currentItemKey, currentItemValue] = originalParamItem.split('=');
                    return [decodeURIComponent(currentItemKey), currentItemValue ? decodeURIComponent(currentItemValue) : ''];
                });
            }
            let foundPage = false;
            // Mijenjamo page query parametar (ako postoji) sa +/-1 ali zadržavamo orginalni redoslijed.
            paramsArray = paramsArray.map(([currentParamKey, currentParamValue]) => {
                if (currentParamKey === 'page') {
                    foundPage = true;
                    return ['page', String(pageNumber)];
                }
                return [currentParamKey, currentParamValue];
            });
            // Ako se desi da page nije u url-u a prepoznat je na SSR-u, dodajemo ga na kraj url-a.
            if (!foundPage) {
                paramsArray.push(['page', String(pageNumber)]);
            }
            // Ako je page 1, uklanjamo ga iz query parametra za link paginacije.
            if (pageNumber === 1) {
                paramsArray = paramsArray.filter(([itemParamKey]) => itemParamKey !== 'page');
            }
            // Finalna izrada query stringa sa očuvanjem orginalnog redoslijeda.
            const finalParams = paramsArray
                .map(([paramArrayKey, paramArrayValue]) => (paramArrayValue ? `${encodeURIComponent(paramArrayKey)}=${encodeURIComponent(paramArrayValue)}` : encodeURIComponent(paramArrayKey)))
                .join('&');

            return finalParams ? `${url}?${finalParams}` : url;
        } catch (error) {
            reportErrorSSR(error);

            return null;
        }
    }

    static generatePreviousLinkSEO(url, page, originalParams) {
        const pageNumber = Number(page);
        return this.buildLinkPaginationSEO(url, pageNumber > 1 ? pageNumber - 1 : 1, originalParams);
    }

    static generateNextLinkSEO(url, page, originalParams) {
        const pageNumber = Number(page);
        return this.buildLinkPaginationSEO(url, pageNumber + 1, originalParams);
    }

    static createWishlistedUnits = (userWishlistRawData) => {
        if (!userWishlistRawData?.length) return [];
        return userWishlistRawData.flatMap((facilityWishlistData) => facilityWishlistData.units);
    };

    static hasDatesInLocalStorageButNotInURL = () => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            const fromQuery = queryParams.get('from');
            const toQuery = queryParams.get('to');
            if (fromQuery || toQuery) return false;
            const storedSearch = localStorage.getItem(ADRIATIC_LAST_SEARCH_PARAMS);
            if (!storedSearch) return false;
            const parsedSearch = JSON.parse(storedSearch);
            const fromLocalStorage = parsedSearch?.params?.from;
            const toLocalStorage = parsedSearch?.params?.to;

            return Boolean(fromLocalStorage && toLocalStorage);
        } catch (error) {
            reportError(error);

            return false;
        }
    };

    static createTrackingObserver(ref, eventName) {
        if (IS_FRAME_SERVICE || !ref.current) return null;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                dispatchTrackingEvent(eventName);
                observer.unobserve(entry.target);
            },
            {
                threshold: 0.25,
            },
        );
        observer.observe(ref.current);

        return observer;
    }

    constructor(props) {
        super(props);
        this.geoLandingContentRef = React.createRef();
        this.helpBannerRef = React.createRef();
        this.nearbyLocationsHelpSectionRef = React.createRef();
        this.helpBannerObserver = null;
        this.pathExtension = pageConfig.searchResultsPage.initialPathExtension;
        this.facilitesExtension = pageConfig.searchResultsPage.secondaryPathExtension;
        this.userWishlistPath = pageConfig.searchResultsPage.userWishlistPath;
        this.searchResultsHelpPath = pageConfig.searchResultsPage.searchResultsHelpPathExtension;
        this.facilitesResultsExtension = 'private/search/number-of-results';
        this.mapResultsExtension = 'accommodation/private/search/results-on-map';
        this.secondaryPathExtensionFirstPrefix = pageConfig.searchResultsPage.secondaryPathExtensionFirstPrefix;
        this.secondaryPathExtensionSecondPrefix = pageConfig.searchResultsPage.secondaryPathExtensionSecondPrefix;
        this.filtersExtension = pageConfig.searchResultsPage.tertiaryPathExtension;
        this.firstNearbyLocationFacilityIndex = null;
        this.isInitialDesktopView = screenService.getInitialInfoIsDesktopView(props.temporaryParametersDataSSR?.isDesktopView, props.ssrParameters?.isDesktopView);
        this.totalResults = SearchResultsContent.extractTotalResults(props.temporarySecondaryDataSSR || props.facilitiesPublicResourcesSSR) || 0;
        this.ssrParameters = props.temporaryParametersDataSSR ?? props.ssrParameters ?? null;
        this.variant = this.ssrParameters?.variant ?? searchResultsWindowManipulationService.getVariant(); // getVariant je fallback za variantu na CSR-u.
        this.variantB = isVariantB(this.variant); // Aktivan test s verzijom "B", naknadno se prebacuje u verziju "A" ili se uklanja, #64264
        this.user = this.ssrParameters?.userJWT && this.ssrParameters.userJWT !== '0'
            ? userData.decodeToken(this.ssrParameters.userJWT)
            : userData.getDecodedUserDataFromLocalStorage();
        this.state = {
            showObserver: false,
            isDesktopView: this.isInitialDesktopView,
            isSmallDevice: false,
            publicResources: props.temporaryPublicResourcesSSR || props.publicResourcesSSR || null,
            facilitiesPublicResources: props.temporarySecondaryDataSSR || props.facilitiesPublicResourcesSSR || null,
            wishlistedUnits: null,
            filtersPublicResources: props.temporaryTertiaryDataSSR || props.filtersPublicResourcesSSR || null,
            facilities: props.temporarySecondaryDataSSR?.results || props.facilitiesPublicResourcesSSR?.results || [],
            facilitiesResultsForMap: props.temporarySecondaryDataSSR?.results || props.facilitiesPublicResourcesSSR?.results || [],
            totalResults: this.totalResults,
            totalResultsForAppliedFilters: this.totalResults,
            page: pageConfig.searchResultsPage.parametersForSecondaryRequest.page,
            errorMsg: null,
            isLoading: false,
            isApplyingFilters: false,
            isScrolled: false,
            mapView: pageConfig.searchResultsPage.parametersForInitialRequest.mapView,
            filterView: false,
            nearbyLocationText: props.temporarySecondaryDataSSR?.nearbyLocationText || props.facilitiesPublicResourcesSSR?.nearbyLocationText || null,
            selectedFilters: {
                // ...LocalStoreSearchParameters.getAllSearchParametersStoredInLocalStorage(),
                sort: SearchResultsContent.setInitialSortOption(props.temporaryPublicResourcesSSR || props.publicResourcesSSR) || 'standard', // Sortiraj rezultate po - string
                ...SearchResultsContent.filtersToReset,
                ne_lat: null,
                ne_lng: null,
                sw_lat: null,
                sw_lng: null, // Pretraga po karti - string
            },
            selectedFiltersCount: 0,
            intervalFilterWasUsed: 0,
            expandTextClick: true,
            showExpandButton: false,
            isTabletFiltersOpened: false,
            isLoginModalOpen: false,
            searchResultsHelpData: this.ssrParameters?.searchResultsHelpData || null,
        };
        // METHODS
        this.getData = this.getData.bind(this);
        // this.loadData = this.loadData.bind(this);
        this.toggleMapView = this.toggleMapView.bind(this);
        this.handleSort = this.handleSort.bind(this);
        this.handleFiltersModal = this.handleFiltersModal.bind(this);
        this.handleApplyFilters = this.handleApplyFilters.bind(this);
        this.handleResetFilters = this.handleResetFilters.bind(this);
        this.handleResetSelectedFilters = this.handleResetSelectedFilters.bind(this);
        this.handleMinChange = this.handleMinChange.bind(this);
        this.handleMaxChange = this.handleMaxChange.bind(this);
        this.handleBathrooms = this.handleBathrooms.bind(this);
        this.handleCenterDistance = this.handleCenterDistance.bind(this);
        this.handleBeachDistance = this.handleBeachDistance.bind(this);
        this.handleAccommodationScore = this.handleAccommodationScore.bind(this);
        this.updateSelectedFilters = this.updateSelectedFilters.bind(this);
        this.updateMapMove = this.updateMapMove.bind(this);
        this.debouncedupdateMapMove = debounce(this.updateMapMove, 500);
        this.handleNumberOfResults = this.handleNumberOfResults.bind(this);
        this.debouncedHandleNumberOfResults = debounce(this.handleNumberOfResults, 600);
        this.debouncedHandleMaxMinValidation = debounce(this.handleMaxMinValidation, 500);
        this.setIntervalFilterWasUsed = this.setIntervalFilterWasUsed.bind(this);
        this.handlePageLoad = this.handlePageLoad.bind(this);
        this.expandTextClick = this.expandTextClick.bind(this);
        this.handleMissingAvailability = this.handleMissingAvailability.bind(this);
        this.handleMissingGuests = this.handleMissingGuests.bind(this);
        this.renderMissingParametersBanner = this.renderMissingParametersBanner.bind(this);
        this.getPaginationData = this.getPaginationData.bind(this);
        this.handlePaginationLoadMoreTracking = this.handlePaginationLoadMoreTracking.bind(this);
    }

    async componentDidMount() {
        this.unsubscribe = screenService.subscribe((isDesktop) => {
            this.setState({ isDesktopView: isDesktop });
        });
        const { facilitiesPublicResources, publicResources, filtersPublicResources, selectedFilters, wishlistedUnits, isLoading } = this.state;
        const initialFilters = {
            ...selectedFilters,
            // ...LocalStoreSearchParameters.getAllSearchParametersStoredInLocalStorage(),
            location_id: searchResultsWindowManipulationService.getLocationID(),
            location: searchResultsWindowManipulationService.getLocationName(),
        };
        const { selectedFiltersUpdates, updatedCount } = SearchResultsContent.parseUrlParams(initialFilters);
        const {
            selectedFiltersUpdates: selectedFiltersUpdates2,
            updatedCount: selectedFiltersCount2,
        } = SearchResultsContent.parseQueryParams(selectedFiltersUpdates, updatedCount);
        // LocalStoreSearchParameters.setAllSearchParametersStoredInLocalStorage(selectedFiltersUpdates2);
        this.setState({
            selectedFilters: selectedFiltersUpdates2,
            selectedFiltersCount: selectedFiltersCount2,
            page: selectedFiltersUpdates2.page ? selectedFiltersUpdates2.page : 1,
        }, () => {
            this.setIntervalFilterWasUsed();
            if (!facilitiesPublicResources || !publicResources || !filtersPublicResources) {
                this.getData();
            } else {
                this.checkMapQueryParam();
                if (!isLoading && !wishlistedUnits && !IS_FRAME_SERVICE) {
                    if (this.user?.id) {
                        apiFetchDataWithoutLangISO(this.userWishlistPath)
                            .then((res) => {
                                this.setState({
                                    wishlistedUnits: SearchResultsContent.createWishlistedUnits(res.data),
                                });
                            })
                            .catch((err) => {
                                reportError(err);
                            });
                    } else {
                        this.setState({ wishlistedUnits: [] });
                    }
                }
            }
            disableElementService.removeServerSideRenderClass();
            if (document.readyState === 'complete') {
                this.handlePageLoad();
            } else {
                window.addEventListener('load', this.handlePageLoad, { once: true });
            }
            window.isReactLoaded = true;
        });
        this.initiallySelectedFilters = { ...selectedFiltersUpdates2 };
        this.setStateForGeolandingContent();
        this.handleResize();
        if (!IS_FRAME_SERVICE && SearchResultsContent.hasDatesInLocalStorageButNotInURL()) {
            dispatchTrackingEvent(COULD_HAVE_DATES_FROM_LOCAL_STORAGE);
        }

        this.helpBannerObserver = SearchResultsContent.createTrackingObserver(
            this.helpBannerRef,
            SEARCH_RESULTS_VIEW_EXTRA_OFFER_BANNER,
        );
        this.nearbyLocationsObserver = SearchResultsContent.createTrackingObserver(
            this.nearbyLocationsHelpSectionRef,
            SEARCH_RESULTS_VIEW_EXTRA_LOCATIONS,
        );

        window.addEventListener('resize', this.handleResize);
    }

    componentDidUpdate(_, prevState) {
        const { page } = this.state;
        const { temporarySecondaryDataSSR, facilitiesPublicResourcesSSR } = this.props;
        if (!temporarySecondaryDataSSR && !facilitiesPublicResourcesSSR && prevState.page !== page) {
            this.updateSEOMetaTags();
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) this.unsubscribe();
        window.removeEventListener('load', this.handlePageLoad);
        window.removeEventListener('resize', this.handleResize);
        this.helpBannerObserver?.disconnect();
        this.nearbyLocationsObserver?.disconnect();
    }

    handleResize = () => {
        // Potrebna bas ova logika sa 991.98px jer se mora podudarati sa logikom "isSmallDevice" iz searchForm-e i searchDocka-a zbog otvaranja slect-a datuma/gostiju na istom view-u.
        this.setState({ isSmallDevice: window.matchMedia('(max-width: 991.98px)').matches });
    };

    handlePageLoad = () => {
        if (!IS_FRAME_SERVICE) {
            this.setState({ showObserver: true });
        }
    };

    handleMinChange(val) {
        if (Number.isNaN(val)) {
            return;
        }
        this.setState(
            (prev) => {
                const { filtersPublicResources } = this.state;
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                const prevMin = selectedFilters.prc_min;
                const priceRangeMin = filtersPublicResources.form.filters.priceRange.rangeSlider.price_range_min;
                selectedFilters.prc_min = val;
                if (prevMin !== priceRangeMin && val === priceRangeMin) {
                    updatedCount -= 1;
                }
                if (prevMin === priceRangeMin && val !== priceRangeMin) {
                    updatedCount += 1;
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleMaxMinValidation('min');
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleMaxChange(val) {
        if (Number.isNaN(val)) {
            return;
        }
        this.setState(
            (prev) => {
                const { filtersPublicResources } = this.state;
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                const prevMax = selectedFilters.prc_max;
                const priceRangeMax = filtersPublicResources.form.filters.priceRange.rangeSlider.price_range_max;
                selectedFilters.prc_max = val;
                if (prevMax !== priceRangeMax && val === priceRangeMax) {
                    updatedCount -= 1;
                }
                if (prevMax === priceRangeMax && val !== priceRangeMax) {
                    updatedCount += 1;
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleMaxMinValidation('max');
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleMaxMinValidation = (updateType) => {
        const { filtersPublicResources } = this.state;
        this.setState((prev) => {
            const { selectedFilters } = prev;
            const updatedFilters = { ...selectedFilters };
            const priceRangeMin = filtersPublicResources.form.filters.priceRange.rangeSlider.price_range_min;
            const priceRangeMax = filtersPublicResources.form.filters.priceRange.rangeSlider.price_range_max;
            let minVal = Math.max(updatedFilters.prc_min, priceRangeMin);
            let maxVal = Math.min(updatedFilters.prc_max, priceRangeMax);
            if (updateType === 'min') {
                if (minVal >= maxVal) {
                    minVal = maxVal - 1;
                }
            }
            if (updateType === 'max') {
                if (maxVal <= minVal) {
                    maxVal = minVal + 1;
                }
            }
            updatedFilters.prc_min = minVal;
            updatedFilters.prc_max = maxVal;
            return {
                selectedFilters: updatedFilters,
            };
        });
    };

    handleCenterDistance(selection) {
        this.setState(
            (prev) => {
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                const prevSelection = selectedFilters.ce_d;
                selectedFilters.ce_d = selection;

                if (parseInt(selection, 10) === 0) {
                    updatedCount -= 1;
                }

                if (parseInt(prevSelection, 10) === 0 && parseInt(selection, 10) !== 0) {
                    updatedCount += 1;
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleBeachDistance(selection) {
        this.setState(
            (prev) => {
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                const prevSelection = selectedFilters.be_d;
                selectedFilters.be_d = selection;

                if (parseInt(selection, 10) === 0) {
                    updatedCount -= 1;
                }

                if (parseInt(prevSelection, 10) === 0 && parseInt(selection, 10) !== 0) {
                    updatedCount += 1;
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleAccommodationScore(selection) {
        this.setState(
            (prev) => {
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                const prevSelection = selectedFilters.f_score;
                selectedFilters.f_score = selection;
                if (selection === '0') {
                    updatedCount -= 1;
                }
                if (prevSelection === '0' && selection !== '0') {
                    updatedCount += 1;
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    async handleNumberOfResults() {
        const stateData = this.state;
        const encryptedParams = this.serializeStateToQueryString(stateData.selectedFilters);

        apiFetchDataWithoutLangISO(this.facilitesResultsExtension, `?${encryptedParams}`)
            .then((res) => {
                if (res.status === 200) {
                    this.setState({
                        totalResults: res.data.numberOfResults,
                    });
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            });
    }

    handleSort(event, newSort = null, reload = true) {
        const selectedSortValue = newSort ?? event.target.value;
        this.setState(
            (prevState) => ({
                selectedFilters: {
                    ...prevState.selectedFilters,
                    sort: selectedSortValue,
                },
            }),
            () => {
                if (reload) {
                    this.handleApplyFilters();
                }
            },
        );
    }

    async handleApplyFilters() {
        // INP: jer loading state re-renderira cijelu stranicu (sve kartice)
        await new Promise((resolve) => {
            runAfterNextPaint(resolve);
        });
        this.setState({ isApplyingFilters: true });
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_FILTERS_SUBMIT);
        const { selectedFilters } = this.state;
        const brevoEmail = this.getCookie('brevo-email');
        if (!IS_FRAME_SERVICE && hasGivenMarketingConsent()) {
            const iterableData = {
                ...(selectedFilters.from && { checkInDate: selectedFilters.from }),
                ...(selectedFilters.to && { checkOutDate: selectedFilters.to }),
                ...(selectedFilters.prc_max && { priceMax: selectedFilters.prc_max }),
                ...(selectedFilters.prc_min && { priceMin: selectedFilters.prc_min }),
                ...((selectedFilters.prc_min || selectedFilters.prc_max) && { currency: 'EUR' }),
                ...(selectedFilters.t && {
                    accommodationType: selectedFilters.t
                        .split('|')
                        .map((type) => type.charAt(0).toUpperCase() + type.slice(1)),
                }),
                ...(selectedFilters.category && {
                    categoryType: selectedFilters.category
                        .split('|')
                        .map((type) => type.charAt(0).toUpperCase() + type.slice(1)),
                }),
                ...(selectedFilters.location && {
                    destination: selectedFilters.location
                        .split(',')
                        .map((loc) => loc.trim()),
                }),
                ...(selectedFilters.location_id && { locationId: selectedFilters.location_id }),
                ...((selectedFilters.persons_adult || selectedFilters.persons_0_3 || selectedFilters.persons_4_11 || selectedFilters.persons_12_17) && {
                    persons:
                        (parseInt(selectedFilters.persons_adult, 10) || 0) + (parseInt(selectedFilters.persons_0_3, 10) || 0) + (parseInt(selectedFilters.persons_4_11, 10) || 0) + (parseInt(selectedFilters.persons_12_17, 10) || 0),
                }),
                ...(selectedFilters.pet === 1 && { pet: true }),
                ...(() => {
                    const preferred = Object.entries(selectedFilters)
                        .filter(([key, value]) => value === 1 && !['pet', 'persons_adult', 'persons_0_3', 'persons_4_11', 'persons_12_17', 'page'].includes(key))
                        .map(([key]) => key);

                    return preferred.length ? { preferredAmenities: preferred } : {};
                })(),
                ...(selectedFilters.f_score && {
                    avgReviewScore: parseFloat(selectedFilters.f_score.replace('+', '')),
                }),
                ...(brevoEmail && { brevo_email: brevoEmail }),
            };
            IterableTrackingService.postData(SEARCH_EVENT, iterableData);
        }
        const encryptedParams = this.serializeStateToQueryString(selectedFilters);
        disableElementService.addDisabledClassOnBody('disableCurrentElementContent--transparent');
        apiFetchData('quick-search-action-url/', `?${encryptedParams}`)
            .then((res) => {
                if (res.status === 200) {
                    let redirectURL = res.data.url;
                    if (res.data.locationName) {
                        const location = encodeURIComponent(res.data.locationName);
                        const locationId = encodeURIComponent(res.data.locationIdFilterValue);
                        redirectURL = `${res.data.url}&location=${location}&locationId=${locationId}`;
                    }
                    window.location.href = redirectURL;
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                this.setState({ isApplyingFilters: false });
                disableElementService.removeDisabledClassOnBody('disableCurrentElementContent--transparent');
            });
    }

    handleResetFilters() {
        this.setState(
            (prevState) => ({
                selectedFiltersCount: 0,
                selectedFilters: {
                    ...prevState.selectedFilters,
                    ...SearchResultsContent.filtersToReset,
                },
            }),
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleResetSelectedFilters() {
        const urlParams = new URLSearchParams(window.location.search);
        let dataParams = {};

        const dataParamsString = searchResultsWindowManipulationService.getParams();
        if (dataParamsString) {
            dataParams = JSON.parse(dataParamsString);
        }

        this.setState((prevState) => {
            const newSelectedFilters = {
                ...prevState.selectedFilters,
            };
            let selectedFiltersCount = 0;
            const filtersDefaults = { ...SearchResultsContent.filtersToReset, sort: '' };
            Object.keys(filtersDefaults).forEach((key) => {
                const existsInUrlParams = urlParams.has(key);
                const existsInDataParams = Object.hasOwn(dataParams, key);
                if ((existsInUrlParams && (key !== 'prc_max' || urlParams.get('prc_max') !== `${SearchResultsContent.maxPriceUpperLimit}`)) || (existsInDataParams && (key !== 'prc_max' || dataParams[key] !== SearchResultsContent.maxPriceUpperLimit))) {
                    selectedFiltersCount += 1;
                    newSelectedFilters[key] = this.initiallySelectedFilters[key];
                } else {
                    newSelectedFilters[key] = filtersDefaults[key];
                }
            });

            return {
                selectedFiltersCount,
                selectedFilters: newSelectedFilters,
            };
        }, () => {
            this.debouncedHandleNumberOfResults();
        });
    }

    handleFiltersModal() {
        const { filterView, publicResources } = this.state;
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_FILTERS_MODAL, { searchResultsTitle: publicResources.title });
        runAfterNextPaint(() => {
            this.setState({ filterView: !filterView });
        });
    }

    handleBathrooms(inputName, eventOperation) {
        this.setState(
            (prev) => {
                const selectedFilters = { ...prev.selectedFilters };
                let updatedCount = prev.selectedFiltersCount;
                if (eventOperation === 'reset') {
                    selectedFilters[inputName] = SearchResultsContent.filtersToReset[inputName];
                    updatedCount -= 1;
                } else if (eventOperation === 'minus') {
                    const prevCount = selectedFilters[inputName];
                    selectedFilters[inputName] = Math.max(prev.selectedFilters[inputName] - 1, 0);
                    if (prevCount === 1 && selectedFilters[inputName] === 0) {
                        updatedCount -= 1;
                    }
                } else {
                    selectedFilters[inputName] = prev.selectedFilters[inputName] + 1;
                    if (prev.selectedFilters[inputName] === 0) {
                        updatedCount += 1;
                    }
                }

                return {
                    selectedFilters,
                    selectedFiltersCount: updatedCount,
                };
            },
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    }

    handleMissingAvailability() {
        const { isSmallDevice } = this.state;
        const element = isSmallDevice ? document.getElementById(DATES_SELECT_SEARCH_DOCK_ID) : document.getElementById(DATES_SELECT_SEARCH_FORM_ID);
        if (!element) {
            return;
        }
        if (isSmallDevice) {
            element.click();
            return;
        }
        element.focus();
    }

    handleMissingGuests() {
        const { isSmallDevice } = this.state;
        const element = isSmallDevice ? document.getElementById(GUEST_SELECT_SEARCH_DOCK_ID) : document.getElementById(GUEST_SELECT_SEARCH_FORM_ID);
        if (!element) {
            return;
        }
        if (isSmallDevice) {
            element.click();
            return;
        }
        element.focus();
    }

    getMissingSearchCardAlert() {
        const { selectedFilters } = this.state;
        const { publicResources } = this.state;
        const searchFormGuests = (parseInt(selectedFilters.persons_adult, 10) || 0) + (parseInt(selectedFilters.persons_0_3, 10) || 0) + (parseInt(selectedFilters.persons_4_11, 10) || 0) + (parseInt(selectedFilters.persons_12_17, 10) || 0);
        const hasGuests = searchFormGuests >= 1;
        const hasDates = Boolean(selectedFilters.from && selectedFilters.to);
        if (!hasDates && !hasGuests) {
            return {
                text: publicResources.cardGuestAdviceData.availabilityAndGuests.text,
                onClick: this.handleMissingAvailability,
            };
        }
        if (hasDates && !hasGuests) {
            return {
                text: publicResources.cardGuestAdviceData.guests.text,
                onClick: this.handleMissingGuests,
            };
        }
        if (!hasDates && hasGuests) {
            return {
                text: publicResources.cardGuestAdviceData.availability.text,
                onClick: this.handleMissingAvailability,
            };
        }

        return null;
    }

    getMissingSearchBanner() {
        const { selectedFilters } = this.state;
        const { publicResources } = this.state;
        const searchFormGuests = (parseInt(selectedFilters.persons_adult, 10) || 0) + (parseInt(selectedFilters.persons_0_3, 10) || 0) + (parseInt(selectedFilters.persons_4_11, 10) || 0) + (parseInt(selectedFilters.persons_12_17, 10) || 0);
        const hasGuests = searchFormGuests >= 1;
        const hasDates = Boolean(selectedFilters.from && selectedFilters.to);
        if (!hasDates && !hasGuests) {
            return {
                text: publicResources.bannerGuestAdviceData.availabilityAndGuests.text,
                buttonText: publicResources.bannerGuestAdviceData.availabilityAndGuests.button,
                onClick: this.handleMissingAvailability,
            };
        }
        if (hasDates && !hasGuests) {
            return {
                text: publicResources.bannerGuestAdviceData.guests.text,
                buttonText: publicResources.bannerGuestAdviceData.guests.button,
                onClick: this.handleMissingGuests,
            };
        }
        if (!hasDates && hasGuests) {
            return {
                text: publicResources.bannerGuestAdviceData.availability.text,
                buttonText: publicResources.bannerGuestAdviceData.availability.button,
                onClick: this.handleMissingAvailability,
            };
        }

        return null;
    }

    setIntervalFilterWasUsed() {
        const { selectedFilters } = this.state;
        this.setState({ intervalFilterWasUsed: selectedFilters.from && selectedFilters.to ? 1 : 0 });
    }

    getData() {
        const stateData = this.state;
        const queryParamsSelectedFilters = stateData.selectedFilters
            ? Object.entries(stateData.selectedFilters)
                .filter(([, value]) => value !== '' && value !== null && value !== 0)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&')
            : '';
        const completedQueryParams = queryParamsSelectedFilters ? `?${queryParamsSelectedFilters}` : '';
        const mapViewValue = stateData.mapView ? 1 : 0;
        this.setState({ isLoading: true });
        Promise.all([
            apiFetchData(`${this.pathExtension}`, `/${mapViewValue}${completedQueryParams}`),
            apiFetchData(`${this.facilitesExtension}${stateData.page}${this.secondaryPathExtensionFirstPrefix}`, completedQueryParams),
            apiFetchData(`${this.facilitesExtension}${stateData.page}${this.secondaryPathExtensionSecondPrefix}`, completedQueryParams),
            apiFetchData(this.filtersExtension),
            this.user?.id && !IS_FRAME_SERVICE ? apiFetchDataWithoutLangISO(this.userWishlistPath) : null,
        ])
            .then(([res1, res2, res2b, res3, res4]) => {
                const firstPartResults = res2?.data?.results ?? [];
                const secondPartResults = res2b?.data?.results ?? [];
                const facilities = [...firstPartResults, ...secondPartResults];
                const nearbyLocationText = res2?.data?.nearbyLocationText ?? res2b?.data?.nearbyLocationText;
                const mergedFacilitiesData = res2?.data
                    ? { ...res2.data, results: facilities, ...(nearbyLocationText && { nearbyLocationText }) }
                    : null;

                const sortOption = SearchResultsContent.setInitialSortOption(res1?.data);
                const totalResults = SearchResultsContent.extractTotalResults(mergedFacilitiesData);
                this.setState((prevState) => ({
                    publicResources: res1?.data,
                    facilitiesPublicResources: mergedFacilitiesData,
                    wishlistedUnits: SearchResultsContent.createWishlistedUnits(res4?.data),
                    nearbyLocationText: mergedFacilitiesData?.nearbyLocationText ?? prevState.nearbyLocationText,
                    filtersPublicResources: res3?.data,
                    facilities: [...prevState.facilities, ...facilities],
                    facilitiesResultsForMap: [...prevState.facilities, ...facilities],
                    selectedFilters: { ...prevState.selectedFilters, sort: sortOption },
                    totalResults,
                    totalResultsForAppliedFilters: SearchResultsContent.extractTotalResults(mergedFacilitiesData),
                    isLoading: false,
                }), () => {
                    this.checkMapQueryParam();
                });
                this.updateSEOMetaTags();
                this.setStateForGeolandingContent();
                // Fallback na CSR-u za dohvat help podataka.
                const totalPages = Math.ceil(totalResults / Number(mergedFacilitiesData?.numberOfResultsPerPage));
                if (totalPages === stateData.page) {
                    const currentURL = window.location.href;
                    const pageEncodedURL = encodeURIComponent(currentURL);
                    apiFetchData(this.searchResultsHelpPath, `?urlEncodedSourceURL=${pageEncodedURL}`)
                        .then((helpResponse) => {
                            if (helpResponse.status === 200) {
                                this.setState((prevState) => ({
                                    ...prevState,
                                    searchResultsHelpData: helpResponse?.data,
                                }));
                                return;
                            }
                            reportError(helpResponse);
                        })
                        .catch((err) => {
                            reportError(err);
                        });
                }
            })
            .catch((err) => {
                this.setState({
                    errorMsg: err,
                    isLoading: false,
                });
                reportError(err);
            });
    }

    setStateForGeolandingContent() {
        const maxHeight = 200;
        const geoLandingPageContentEl = this.geoLandingContentRef.current;
        if (geoLandingPageContentEl && geoLandingPageContentEl.scrollHeight > maxHeight) {
            this.setState({ showExpandButton: true, expandTextClick: false });
        }
    }

    getCookie(name) { // eslint-disable-line class-methods-use-this
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    }

    isDiscountTypeAttractiveOffer = () => {
        const { selectedFilters } = this.state;
        return selectedFilters.dc_t === 'selected_attractive_offer';
    };

    updateWishlistedUnits = (wishlistUnitsUpdates) => {
        const { unitsToAdd, unitsToRemove } = wishlistUnitsUpdates;
        this.setState((prevState) => ({
            wishlistedUnits: [...prevState.wishlistedUnits, ...(unitsToAdd ?? [])].filter((unitId) => !unitsToRemove || !unitsToRemove.includes(unitId)),
        }));
    };

    updateSEOMetaTags = () => {
        const { page, totalResults, facilitiesPublicResources } = this.state;
        if (!facilitiesPublicResources) return;
        const { numberOfResultsPerPage } = facilitiesPublicResources;
        const totalPages = Math.ceil(totalResults / numberOfResultsPerPage);

        const url = new URL(window.location.href);

        const prevLink = document.querySelector('link[rel="prev"]') || document.createElement('link');
        if (page > 1) {
            url.searchParams.set('page', page - 1);
            prevLink.setAttribute('rel', 'prev');
            prevLink.setAttribute('href', url.toString());
            if (!document.head.contains(prevLink)) {
                document.head.appendChild(prevLink);
            }
        } else if (document.head.contains(prevLink)) {
            prevLink.remove();
        }

        const nextLink = document.querySelector('link[rel="next"]') || document.createElement('link');
        if (page < totalPages) {
            url.searchParams.set('page', page + 1);
            nextLink.setAttribute('rel', 'next');
            nextLink.setAttribute('href', url.toString());
            if (!document.head.contains(nextLink)) {
                document.head.appendChild(nextLink);
            }
        } else if (document.head.contains(nextLink)) {
            nextLink.remove();
        }
    };

    handlePaginationLoadMoreTracking = (page) => {
        const { publicResources } = this.state;
        if (!IS_FRAME_SERVICE) {
            dispatchTrackingEvent(SEARCH_RESULTS_LOAD_MORE, {
                searchResultsTitle: `${publicResources.title} - ${page}`,
            });
        }
    };

    getPaginationData = () => {
        const { facilitiesPublicResources, totalResultsForAppliedFilters } = this.state;
        const numberOfResultsPerPage = facilitiesPublicResources?.numberOfResultsPerPage;
        if (!numberOfResultsPerPage) {
            return null;
        }
        const totalPages = Math.ceil(totalResultsForAppliedFilters / numberOfResultsPerPage);
        const pathname = isClientSideRendering ? window.location.pathname : this.ssrParameters.windowLocationPathname;
        const search = isClientSideRendering ? window.location.search : this.ssrParameters.windowLocationSearch;

        return { totalPages, pathname, search };
    };

    updateSelectedFilters = (updatedFilters, redirect) => {
        this.setState(
            (prevState) => ({
                selectedFilters: {
                    ...prevState.selectedFilters,
                    ...updatedFilters,
                },
            }),
            async () => {
                const stateData = this.state;
                const brevoEmail = this.getCookie('brevo_email');
                const iterableData = {
                    ...(stateData.selectedFilters.from && { checkInDate: stateData.selectedFilters.from }),
                    ...(stateData.selectedFilters.to && { checkOutDate: stateData.selectedFilters.to }),
                    ...(stateData.selectedFilters.selectedLocation && {
                        destination: stateData.selectedFilters.selectedLocation
                            .split(',')
                            .map((loc) => loc.trim()),
                    }),
                    ...(stateData.selectedFilters.location_id && { locationId: stateData.selectedFilters.location_id }),
                    ...((stateData.selectedFilters.persons_adult || stateData.selectedFilters.persons_0_3 || stateData.selectedFilters.persons_4_11 || stateData.selectedFilters.persons_12_17) && {
                        persons:
                            (parseInt(stateData.selectedFilters.persons_adult, 10) || 0) + (parseInt(stateData.selectedFilters.persons_0_3, 10) || 0) + (parseInt(stateData.selectedFilters.persons_4_11, 10) || 0) + (parseInt(stateData.selectedFilters.persons_12_17, 10) || 0),
                    }),
                    ...(brevoEmail && { brevo_email: brevoEmail }),
                };
                // LocalStoreSearchParameters.setAllSearchParametersStoredInLocalStorage(stateData.selectedFilters);
                if (redirect) {
                    if (this.user?.id && hasGivenMarketingConsent()) {
                        IterableTrackingService.postData(SEARCH_EVENT, iterableData);
                    }
                    const encryptedParams = this.serializeStateToQueryString(stateData.selectedFilters);
                    disableElementService.addDisabledClassOnBody('disableCurrentElementContent--transparent');
                    apiFetchData('quick-search-action-url/', `?${encryptedParams}`)
                        .then((res) => {
                            if (res.status === 200) {
                                let redirectURL = res.data.url;
                                if (res.data.locationName) {
                                    const location = encodeURIComponent(res.data.locationName);
                                    const locationId = encodeURIComponent(res.data.locationIdFilterValue);
                                    redirectURL = `${res.data.url}&location=${location}&locationId=${locationId}`;
                                }
                                window.location.href = redirectURL;
                                return;
                            }
                            reportError(res);
                        })
                        .catch((err) => {
                            reportError(err);
                        })
                        .finally(() => {
                            disableElementService.removeDisabledClassOnBody('disableCurrentElementContent--transparent');
                        });
                }
            },
        );
    };

    updateMapMove = (updatedFilters) => {
        this.setState(
            (prevState) => ({
                selectedFilters: {
                    ...prevState.selectedFilters,
                    ...updatedFilters,
                },
            }),
            async () => {
                const stateData = this.state;
                const encryptedParams = this.serializeStateToQueryString(stateData.selectedFilters);
                apiFetchData(`${this.mapResultsExtension}/1/`, `?${encryptedParams}`)
                    .then((res) => {
                        if (res.status === 200) {
                            const updatedFacilitiesPublicResources = { ...stateData.facilitiesPublicResources };
                            updatedFacilitiesPublicResources.totalResults = res.data.totalResults;
                            updatedFacilitiesPublicResources.zeroResultsPageText = res.data.zeroResultsPageText;
                            this.setState({
                                // facilities: res.data.results,
                                // facilitiesPublicResources: updatedFacilitiesPublicResources,
                                // Prepravljeno na način da smo dodali novi state a prethodna dva zakomentirali, razlog:
                                // želimo update rezultata na mapi prilikom interakcije na mapi, ali ne želim o update stranice rezultata pretrage nakon zatvaranja mape
                                facilitiesResultsForMap: res.data.results,
                            });
                            return;
                        }
                        reportError(res);
                    })
                    .catch((err) => {
                        reportError(err);
                    });
            },
        );
    };

    handleFilterChange = (filterType, value) => {
        this.setState(
            (prevState) => {
                const selectedFilters = { ...prevState.selectedFilters };
                let updatedCount = prevState.selectedFiltersCount;

                if (value) {
                    if (!selectedFilters[filterType] || selectedFilters[filterType] === 0) {
                        if (filterType === 'dc_t') {
                            selectedFilters[filterType] = value;
                        } else {
                            selectedFilters[filterType] = 1;
                        }
                        updatedCount += 1;
                    } else {
                        if (filterType === 'dc_t') {
                            selectedFilters[filterType] = '';
                        } else {
                            selectedFilters[filterType] = 0;
                        }
                        updatedCount -= 1;
                    }
                } else {
                    selectedFilters[filterType] = selectedFilters[filterType] === 0 ? 1 : 0;
                    if (selectedFilters[filterType] === 1) {
                        updatedCount += 1;
                    } else {
                        updatedCount -= 1;
                    }
                }

                return { selectedFilters, selectedFiltersCount: updatedCount };
            },
            () => {
                this.debouncedHandleNumberOfResults();
            },
        );
    };

    handleCheckboxChange = (name, value) => {
        const { selectedFilters, selectedFiltersCount } = this.state;
        let updatedCount = selectedFiltersCount;
        const filtersArray = selectedFilters[name] ? selectedFilters[name].split('|') : [];
        if (filtersArray.includes(value)) {
            const updatedFiltersArray = filtersArray.filter((item) => item !== value);
            selectedFilters[name] = updatedFiltersArray.join('|');
            updatedCount -= 1;
        } else {
            selectedFilters[name] = filtersArray.length > 0 ? `${selectedFilters[name]}|${value}` : value;
            updatedCount += 1;
        }
        selectedFilters[name] = selectedFilters[name].replace(/\+/g, '+');
        this.setState({ selectedFilters, selectedFiltersCount: updatedCount }, () => {
            this.debouncedHandleNumberOfResults();
        });
    };

    prepareGTMData = () => {
        const items = [];
        const { facilities } = this.state;
        facilities.forEach((facility, facilityIndexOnPage) => {
            facility.units.forEach((unit) => {
                const baseAnalyticsItem = buildBaseAnalyticsItem({ unitItem: unit });
                const gtmItem = {
                    ...baseAnalyticsItem,
                    currency: 'EUR',
                    index: facilityIndexOnPage + 1,
                    item_category: facility.categoryName ? facility.categoryName : 'Rezultati pretrage',
                    item_list_id: facility.categoryId ? facility.categoryId : 'search',
                    item_list_name: facility.categorySlug ? facility.categorySlug : 'Kategorija Rezultati pretrage',
                };
                items.push(gtmItem);
            });
        });

        return items;
    };

    checkMapQueryParam = () => {
        const { mapView } = this.state;
        const urlParams = new URLSearchParams(window.location.search);
        const mapParam = urlParams.get('map');

        if (mapParam === '1' || mapParam === 'true') {
            if (!mapView) {
                this.toggleMapView();
            }
        }
    };

    // TODO: Disable-ano samo zbog toga što je u planu refactor klasnih komponenti.
    // eslint-disable-next-line class-methods-use-this
    handleSearchResultsHelpBannerLinkClick = (event) => {
        if (!IS_FRAME_SERVICE && (event.type === 'click' || (event.type === 'auxclick' && event.button === 1))) {
            dispatchTrackingEvent(SEARCH_RESULTS_EXTRA_OFFER_BANNER);
        }
    };

    expandTextClick() {
        const { expandTextClick } = this.state;
        this.setState({ expandTextClick: !expandTextClick });
    }

    serializeStateToQueryString() {
        const { selectedFilters } = this.state;
        const integerKeys = ['persons_0_3', 'persons_adult', 'persons_12_17', 'persons_4_11'];
        const queryString = Object.keys(selectedFilters)
            .filter((key) => {
                const value = selectedFilters[key];
                const isValueTruthy = value !== false && value !== null && value !== undefined && value !== '' && value !== 0;
                const isPrcMaxWithUpperLimitValue = key === 'prc_max' && value >= SearchResultsContent.maxPriceUpperLimit;
                return isValueTruthy && !isPrcMaxWithUpperLimitValue;
            })
            .map((key) => {
                let value = selectedFilters[key];
                if (integerKeys.includes(key)) {
                    value = parseInt(value, 10);
                }
                if (Array.isArray(value) || typeof value === 'object') {
                    value = JSON.stringify(value);
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            })
            .join('&');

        return queryString;
    }

    toggleMapView() {
        // INP: jer prikaz/skrivanje mape re-renderira cijelu stranicu i zaključava scroll body-ja.
        runAfterNextPaint(() => {
            const { mapView } = this.state;
            const newMapView = !mapView;
            this.setState({ mapView: newMapView, filterView: false }, () => {
                if (newMapView && IS_FRAME_SERVICE) {
                    getDesiredPositionInsideFrameFromTopSoModalCanBeInsideViewport();
                }
                const mainHeader = document.getElementById('mainHeader');
                if (mainHeader) {
                    if (newMapView) {
                        mainHeader.classList.add('bg-transparent');
                        mainHeader.classList.add('mobile-slide-down');
                    } else {
                        mainHeader.classList.remove('bg-transparent');
                    }
                }
            });
        });
    }

    isFirstNearbyLocationFacility(index) {
        if (this.firstNearbyLocationFacilityIndex === null) {
            this.firstNearbyLocationFacilityIndex = index;
        }

        return this.firstNearbyLocationFacilityIndex === index;
    }

    renderMissingParametersBanner() {
        const banner = this.getMissingSearchBanner();
        if (!banner) {
            return null;
        }

        return (
            <button
                type="button"
                className="search-results__banner"
                onClick={banner.onClick}
            >
                <span className="search-results__banner-txt-wrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <mask
                            id="mask0_6961_30126"
                            style={{ maskType: 'alpha' }}
                            maskUnits="userSpaceOnUse"
                            x="0"
                            y="0"
                            width="24"
                            height="24"
                        >
                            <rect width="24" height="24" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_6961_30126)">
                            <path d="M12 13.9998C11.7167 13.9998 11.4792 13.9039 11.2875 13.7123C11.0958 13.5206 11 13.2831 11 12.9998C11 12.7164 11.0958 12.4789 11.2875 12.2873C11.4792 12.0956 11.7167 11.9998 12 11.9998C12.2833 11.9998 12.5208 12.0956 12.7125 12.2873C12.9042 12.4789 13 12.7164 13 12.9998C13 13.2831 12.9042 13.5206 12.7125 13.7123C12.5208 13.9039 12.2833 13.9998 12 13.9998ZM7.2875 13.7123C7.09583 13.5206 7 13.2831 7 12.9998C7 12.7164 7.09583 12.4789 7.2875 12.2873C7.47917 12.0956 7.71667 11.9998 8 11.9998C8.28333 11.9998 8.52083 12.0956 8.7125 12.2873C8.90417 12.4789 9 12.7164 9 12.9998C9 13.2831 8.90417 13.5206 8.7125 13.7123C8.52083 13.9039 8.28333 13.9998 8 13.9998C7.71667 13.9998 7.47917 13.9039 7.2875 13.7123ZM12 17.9998C11.7167 17.9998 11.4792 17.9039 11.2875 17.7123C11.0958 17.5206 11 17.2831 11 16.9998C11 16.7164 11.0958 16.4789 11.2875 16.2873C11.4792 16.0956 11.7167 15.9998 12 15.9998C12.2833 15.9998 12.5208 16.0956 12.7125 16.2873C12.9042 16.4789 13 16.7164 13 16.9998C13 17.2831 12.9042 17.5206 12.7125 17.7123C12.5208 17.9039 12.2833 17.9998 12 17.9998ZM7.2875 17.7123C7.09583 17.5206 7 17.2831 7 16.9998C7 16.7164 7.09583 16.4789 7.2875 16.2873C7.47917 16.0956 7.71667 15.9998 8 15.9998C8.28333 15.9998 8.52083 16.0956 8.7125 16.2873C8.90417 16.4789 9 16.7164 9 16.9998C9 17.2831 8.90417 17.5206 8.7125 17.7123C8.52083 17.9039 8.28333 17.9998 8 17.9998C7.71667 17.9998 7.47917 17.9039 7.2875 17.7123Z" fill="#0574B0" />
                            <path d="M19 10H5V20V22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6C3 5.45 3.19583 4.97917 3.5875 4.5875C3.97917 4.19583 4.45 4 5 4H6V2H8V4H16V2H18V4H19C19.55 4 20.0208 4.19583 20.4125 4.5875C20.8042 4.97917 21 5.45 21 6V12H19V10ZM5 8H19V6H5V8Z" fill="#0574B0" />
                            <path d="M15.9109 17.8388C15.4703 17.3982 15.25 16.8685 15.25 16.2498C15.25 15.631 15.4703 15.1013 15.9109 14.6607C16.3516 14.2201 16.8812 13.9998 17.5 13.9998C18.1188 13.9998 18.6484 14.2201 19.0891 14.6607C19.5297 15.1013 19.75 15.631 19.75 16.2498C19.75 16.8685 19.5297 17.3982 19.0891 17.8388C18.6484 18.2794 18.1188 18.4998 17.5 18.4998C16.8812 18.4998 16.3516 18.2794 15.9109 17.8388ZM13 22.9998V21.4248C13 21.106 13.082 20.813 13.2461 20.5458C13.4102 20.2787 13.6281 20.0748 13.9 19.9341C14.4813 19.6435 15.0719 19.4255 15.6719 19.2802C16.2719 19.1349 16.8812 19.0623 17.5 19.0623C18.1188 19.0623 18.7281 19.1349 19.3281 19.2802C19.9281 19.4255 20.5188 19.6435 21.1 19.9341C21.3719 20.0748 21.5898 20.2787 21.7539 20.5458C21.918 20.813 22 21.106 22 21.4248V22.9998H13ZM17.5 17.3748C17.8094 17.3748 18.0742 17.2646 18.2945 17.0443C18.5148 16.824 18.625 16.5591 18.625 16.2498C18.625 15.9404 18.5148 15.6755 18.2945 15.4552C18.0742 15.2349 17.8094 15.1248 17.5 15.1248C17.1906 15.1248 16.9258 15.2349 16.7055 15.4552C16.4852 15.6755 16.375 15.9404 16.375 16.2498C16.375 16.5591 16.4852 16.824 16.7055 17.0443C16.9258 17.2646 17.1906 17.3748 17.5 17.3748ZM14.125 21.8748H20.875V21.4248C20.875 21.3216 20.8492 21.2279 20.7977 21.1435C20.7461 21.0591 20.6781 20.9935 20.5938 20.9466C20.0875 20.6935 19.5766 20.5037 19.0609 20.3771C18.5453 20.2505 18.025 20.1873 17.5 20.1873C16.975 20.1873 16.4547 20.2505 15.9391 20.3771C15.4234 20.5037 14.9125 20.6935 14.4062 20.9466C14.3219 20.9935 14.2539 21.0591 14.2023 21.1435C14.1508 21.2279 14.125 21.3216 14.125 21.4248V21.8748Z" fill="#0574B0" />
                        </g>
                    </svg>
                    <span
                        className="search-results__banner-txt"
                        dangerouslySetInnerHTML={{ __html: banner.text }}
                    />
                </span>
                <span className="search-results__banner-btn">
                    {banner.buttonText}
                </span>
            </button>
        );
    }

    renderTooFewResultsBanner = () => {
        const { searchResultsHelpData } = this.state;
        const tooFewResultsHelpData = searchResultsHelpData.suggestions.tooFewResults;

        return (
            <div ref={this.helpBannerRef} className="search-results__banner search-results__banner--too-few-results">
                <p className="search-results__banner-txt-wrap">
                    <svg className="search-results__banner-svg" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#0574B0">
                        <path d="M680-80v-120H560v-80h120v-120h80v120h120v80H760v120h-80Zm-480-80q-33 0-56.5-23.5T120-240v-480q0-33 23.5-56.5T200-800h40v-80h80v80h240v-80h80v80h40q33 0 56.5 23.5T760-720v244q-20-3-40-3t-40 3v-84H200v320h280q0 20 3 40t11 40H200Zm0-480h480v-80H200v80Zm0 0v-80 80Z" />
                    </svg>
                    <span
                        className="search-results__banner-txt"
                        dangerouslySetInnerHTML={{ __html: tooFewResultsHelpData.explanationHTMLText }}
                    />
                </p>
                <a
                    href={tooFewResultsHelpData.url}
                    className="search-results__banner-btn search-results__banner-btn--link"
                    onClick={this.handleSearchResultsHelpBannerLinkClick}
                    onAuxClick={this.handleSearchResultsHelpBannerLinkClick}
                >
                    {tooFewResultsHelpData.text}
                </a>
            </div>
        );
    };

    renderNearbyLocationsHelpSection = () => {
        const { searchResultsHelpData } = this.state;
        const nearbyLocationsHelpData = searchResultsHelpData.suggestions.nearByLocations;

        return (
            <div ref={this.nearbyLocationsHelpSectionRef} className="search-results__nearby-locations">
                <h3 className="search-results__nearby-locations-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.9333 20 13.8125 19.8542 14.6375 19.5625C15.4625 19.2708 16.2167 18.8583 16.9 18.325L15.475 16.9C14.9917 17.25 14.4542 17.5208 13.8625 17.7125C13.2708 17.9042 12.65 18 12 18C10.3333 18 8.91667 17.4167 7.75 16.25C6.58333 15.0833 6 13.6667 6 12C6 10.3333 6.58333 8.91667 7.75 7.75C8.91667 6.58333 10.3333 6 12 6C13.6667 6 15.0833 6.58333 16.25 7.75C17.4167 8.91667 18 10.3333 18 12C18 12.65 17.9 13.275 17.7 13.875C17.5 14.475 17.225 15.0167 16.875 15.5L18.3 16.925C18.8333 16.2417 19.25 15.4833 19.55 14.65C19.85 13.8167 20 12.9333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20ZM12 16C12.3667 16 12.7208 15.9542 13.0625 15.8625C13.4042 15.7708 13.725 15.6333 14.025 15.45L12.5 13.925C12.4167 13.9583 12.3333 13.9792 12.25 13.9875C12.1667 13.9958 12.0833 14 12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.1 13.9958 12.1958 13.9875 12.2875C13.9792 12.3792 13.9583 12.4667 13.925 12.55L15.425 14.05C15.6083 13.75 15.75 13.4292 15.85 13.0875C15.95 12.7458 16 12.3833 16 12C16 10.9 15.6083 9.95833 14.825 9.175C14.0417 8.39167 13.1 8 12 8C10.9 8 9.95833 8.39167 9.175 9.175C8.39167 9.95833 8 10.9 8 12C8 13.1 8.39167 14.0417 9.175 14.825C9.95833 15.6083 10.9 16 12 16Z" fill="#0574B0" />
                    </svg>
                    {nearbyLocationsHelpData.explanationText}
                </h3>
                <div className="search-results__nearby-locations-items">
                    {nearbyLocationsHelpData.locations.map((location) => (
                        <a
                            key={location.locationName}
                            href={location.url}
                            className="search-results__nearby-locations-item"
                            onClick={() => {
                                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_EXTRA_LOCATIONS);
                            }}
                            onAuxClick={(event) => {
                                if (!IS_FRAME_SERVICE && event.button === 1) {
                                    dispatchTrackingEvent(SEARCH_RESULTS_EXTRA_LOCATIONS);
                                }
                            }}
                        >
                            <span className="search-results__nearby-locations-item-title">
                                {location.locationName}
                            </span>
                            <span className="search-results__nearby-locations-item-subtitle">
                                {location.numberOfOffersInLocation}
                            </span>
                        </a>
                    ))}
                </div>
            </div>
        );
    };

    // async loadData() {
    //     const stateData = this.state;
    //     const encryptedParams = this.serializeStateToQueryString(stateData.selectedFilters);
    //     this.setState({ isLoadingMoreSearchResults: true });
    //     serviceDataNew
    //         .getData(`private/search/results/${stateData.page}/${stateData.limit}/`, `?${encryptedParams}`)
    //         .then((res) => {
    //             if (res.status === 200) {
    //                 const totalResults = parseInt(res.data.totalResults.split(':')[1].trim(), 10);
    //                 const results = Array.isArray(res.data.results) ? res.data.results : [];
    //                 this.setState((prevState) => ({
    //                     facilities: [...prevState.facilities, ...results],
    //                     nearbyLocationText: res.data.nearbyLocationText ?? prevState.nearbyLocationText,
    //                     totalResults,
    //                 }));
    //                 return;
    //             }
    //             reportError(res);
    //         })
    //         .catch((err) => {
    //             reportError(err);
    //         })
    //         .finally(() => {
    //             this.setState({
    //                 isLoadingMoreSearchResults: false,
    //             });
    //         });
    // }

    // TODO: Disable-ano samo zbog toga što je u planu refactor klasnih komponenti.
    // eslint-disable-next-line class-methods-use-this
    renderSkeletonSmallDeviceCollapsedForm = () => (
        <div className="searchFormWrap">
            <div className="mainSearchForm">
                <div className="main-search-form-accordion__items d-lg-none">
                    {[...Array(1)].map((_, index) => (
                        <div className="main-search-form-accordion__item" key={index}>
                            <div className="main-search-form-accordion__item-left">
                                <Skeleton circle={true} height={16} width={20} />
                                <Skeleton width={150} height={16} style={{ marginLeft: '8px' }} />
                            </div>
                            <div className="main-search-form-accordion__item-right">
                                <Skeleton height={16} width={20} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // TODO: Disable-ano samo zbog toga što je u planu refactor klasnih komponenti.
    // eslint-disable-next-line class-methods-use-this
    renderSkeletonDesktop = () => (
        <div className="searchFormWrap skeleton">
            <div className="mainSearchForm skeleton">
                <div className="inputLocationOuter d-lg-block d-none mr-2">
                    <Skeleton height={58} width="100%" />
                </div>
                <div className="inputDateRangeOuter d-lg-block d-none mr-2">
                    <Skeleton height={58} width="100%" />
                </div>
                <div className="inputGuestsOuter d-lg-block d-none mr-2">
                    <Skeleton height={58} width="100%" />
                </div>
                <Skeleton height={40} width={40} circle={true} containerClassName="submit-search" />
            </div>
        </div>
    );

    render() {
        const {
            isDesktopView,
            isLoading,
            isApplyingFilters,
            facilities,
            facilitiesResultsForMap,
            errorMsg,
            publicResources,
            selectedFiltersCount,
            filtersPublicResources,
            facilitiesPublicResources,
            filterView,
            selectedFilters,
            isScrolled,
            totalResults,
            mapView,
            nearbyLocationText,
            page,
            intervalFilterWasUsed,
            expandTextClick,
            showExpandButton,
            defaultCheckedBeach,
            defaultCheckedCenter,
            isTabletFiltersOpened,
            showObserver,
            isLoginModalOpen,
            wishlistedUnits,
            searchResultsHelpData,
        } = this.state;
        const { ssrSearchForm, temporarySearchFormDataSSR } = this.props;
        // TODO: Potrebno popraviti, izbjeći logiku unutar rendera jer se odrađiva na svakom re-rendu.
        const bottomContent = publicResources?.geoLandingPage ?? publicResources?.landingPage ?? publicResources?.discountBanner;
        const isBottomContentDiscountBanner = !!publicResources?.discountBanner;
        const accomodationTypeStatisticsCount = bottomContent?.accomodationTypeStatistics?.length ?? 0;
        const accomodationNumberCount = bottomContent?.accommodationNumber ? 1 : 0;
        const lowestPriceCount = bottomContent?.lowestPrice ? 1 : 0;
        const accomodationStatisticsTotalCount = accomodationTypeStatisticsCount + accomodationNumberCount + lowestPriceCount;
        const smallDevice = isClientSideRendering && window.matchMedia('(max-width: 767.98px)').matches;
        // TODO: Potrebno popraviti logiku sa skeleton, na SSR-u ne znam da li se radi o smallDevice (max-width: 767.98px),
        // za sada smo se osigurali da ako je samallDevice false(SSR ga ne moze izracunat pa je false), provjerit
        // cemo je li isDesktopView false (nju mozemo dobit na SSR preko device type-a) razlog isDesktopView
        // sa SSR se prepoznaje na osnovu device-a, a u ovome slučaju se traži da width bude ispod 767.98px
        // #62680 Na mobilnim uređajima quick search na rezultatima pretrage defaultno držati zatvorenim
        const currentSearchFormLoader = smallDevice || !isDesktopView ? this.renderSkeletonSmallDeviceCollapsedForm() : this.renderSkeletonDesktop();
        const gtmData = this.prepareGTMData();
        const bigimg = 0;
        const paginationData = this.getPaginationData();

        return (
            <>
                <BreadCrumbs
                    breadcrumbs={publicResources?.breadcrumbs}
                    breadcrumbsStructuredData={publicResources?.breadcrumbsStructuredData}
                    isLoading={isLoading}
                    renderInReactContainer={true}
                />
                {errorMsg && <AxiosError />}
                <div className={`search-results-content-search ${isScrolled ? 'scrolled' : ''}`}>
                    <SearchForm
                        ssrSearchForm={ssrSearchForm || temporarySearchFormDataSSR || null}
                        ssrParameters={this.ssrParameters}
                        update={this.updateSelectedFilters}
                        searchFormLoader={currentSearchFormLoader}
                        isSearchResults={true}
                    />
                </div>
                {isLoading && <Loader />}
                {mapView && (
                    <div className="search-results__map-map-and-filters-buttons">
                        {/* <button
                            type="button"
                            className="form__button form__button--primary"
                            data-toggle="modal"
                            onClick={this.handleFiltersModal}
                            disabled={isLoading}
                        >
                            <MdOutlineTune size={22} />
                            <span>{publicResources.filterButton}</span>
                        </button> */}
                        <button
                            type="button"
                            className="form__button form__button--secondary search-results__map-close-button"
                            onClick={this.toggleMapView}
                            disabled={isLoading}
                        >
                            <BiCollapseAlt className="search-results__map-close-button-icon" />
                            <span>{publicResources.closeMapButton}</span>
                        </button>
                    </div>
                )}
                {publicResources && (
                    <>
                        {showObserver && <SearchResultObserver trackingData={gtmData} isVariantB={this.variantB} />}
                        <LoginForMoreBenefits isLoginModalOpenOutsideControl={isLoginModalOpen} onModalClose={() => this.setState({ isLoginModalOpen: false })} />
                        <div className="row pageContent">
                            <div className="searchResultsContent col-12">
                                <div className="search-results__container">
                                    <div className="search-results__left">
                                        {!isTabletFiltersOpened && (
                                            <div className="search-results__left-tablet-open-filters-buttons">
                                                <button
                                                    type="button"
                                                    className="search-results__left-tablet-open-filters-button"
                                                    onClick={() => this.setState({ isTabletFiltersOpened: true })}
                                                    aria-label="Open filters"
                                                >
                                                    <MdOutlineMap size="24" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="search-results__left-tablet-open-filters-button"
                                                    onClick={() => this.setState({ isTabletFiltersOpened: true })}
                                                    aria-label="Open filters"
                                                >
                                                    <MdOutlineFilterList size="24" />
                                                </button>
                                            </div>
                                        )}
                                        <div className={`search-results__left-content-container ${!isTabletFiltersOpened ? 'search-results__left-content-container--tablet-closed' : ''}`}>
                                            <div className="search-results__left-content">
                                                <button
                                                    type="button"
                                                    className="search-results__left-tablet-close-filters-button"
                                                    onClick={() => this.setState({ isTabletFiltersOpened: false })}
                                                    aria-label="Close filters"
                                                >
                                                    <MdOutlineMenuOpen size="24" />
                                                </button>
                                                {/* Prikaz samo na mobilnom view-u. */}
                                                <button
                                                    type="button"
                                                    className="search-results__map-button d-none d-lg-flex"
                                                    onClick={this.toggleMapView}
                                                >
                                                    {publicResources.searchMapButton}
                                                </button>
                                                <div className={mapView && filterView ? 'search-results__map-filters' : ''}>
                                                    <Filters
                                                        data={{
                                                            ...filtersPublicResources.form,
                                                            showMoreText: publicResources.showMore,
                                                            showLessText: publicResources.showLess,
                                                        }}
                                                        sortData={publicResources.sort}
                                                        handleSort={this.handleSort}
                                                        isLoading={isApplyingFilters}
                                                        selectedFilters={selectedFilters}
                                                        selectedFiltersCount={selectedFiltersCount}
                                                        handleFilterChange={this.handleFilterChange}
                                                        handleCheckboxChange={this.handleCheckboxChange}
                                                        handleMinChange={this.handleMinChange}
                                                        handleMaxChange={this.handleMaxChange}
                                                        maxPriceUpperLimit={this.maxPriceUpperLimit}
                                                        handleBathrooms={this.handleBathrooms}
                                                        handleCenterDistance={this.handleCenterDistance}
                                                        handleBeachDistance={this.handleBeachDistance}
                                                        defaultCheckedCenter={defaultCheckedCenter}
                                                        defaultCheckedBeach={defaultCheckedBeach}
                                                        handleAccommodationScore={this.handleAccommodationScore}
                                                        handleResetFilters={this.handleResetFilters}
                                                        filtersDefaults={SearchResultsContent.filtersToReset}
                                                        initiallySelectedFilters={this.initiallySelectedFilters}
                                                        handleApplyFilters={this.handleApplyFilters}
                                                        searchResultsCount={totalResults}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="search-results__cards">
                                        <div className="search-results__cards-header">
                                            <div>
                                                <h1 className="search-results__title">{this.isDiscountTypeAttractiveOffer() && <>&#128293;</>} {publicResources.title}</h1>
                                                <p className="search-results__num-results">{facilitiesPublicResources.totalAccommodations}</p>
                                            </div>
                                            <div className="search-results-content-sort d-none d-lg-flex">
                                                <label htmlFor="sortSelectDesktop" className="search-results-sort__title">{publicResources.sort.title}</label>
                                                <select
                                                    id="sortSelectDesktop"
                                                    className="search-results-content-sort-select"
                                                    onChange={this.handleSort}
                                                    value={selectedFilters.sort}
                                                >
                                                    {publicResources.sort.options.map((item, index) => (
                                                        <option value={item.value} key={index}>
                                                            {item.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        {/* Prikaz samo na mobilnom view-u. */}
                                        <div className="search-results__cards-btns d-flex d-lg-none">
                                            <button
                                                type="button"
                                                className="search-results__btn-map"
                                                onClick={this.toggleMapView}
                                            >
                                                <MdOutlineMap size="20" />
                                                {publicResources.mapButton}
                                            </button>
                                            <button
                                                type="button"
                                                className="search-results__btn-filter"
                                                onClick={this.handleFiltersModal}
                                            >
                                                <MdOutlineTune size="20" />
                                                <span>{publicResources.filtersButton}</span>
                                                {selectedFiltersCount > 0 && (
                                                    <span className="search-results__btn-filter-count">
                                                        {selectedFiltersCount}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                        {facilitiesPublicResources.showGuestAdvice && this.renderMissingParametersBanner()}
                                        {facilities?.map((facility, index) => (
                                            <React.Fragment key={index}>
                                                {facility.isNearbyLocation && this.isFirstNearbyLocationFacility(index) && (
                                                    <div className="search-results__nearby-location-alert">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.9333 20 13.8125 19.8542 14.6375 19.5625C15.4625 19.2708 16.2167 18.8583 16.9 18.325L15.475 16.9C14.9917 17.25 14.4542 17.5208 13.8625 17.7125C13.2708 17.9042 12.65 18 12 18C10.3333 18 8.91667 17.4167 7.75 16.25C6.58333 15.0833 6 13.6667 6 12C6 10.3333 6.58333 8.91667 7.75 7.75C8.91667 6.58333 10.3333 6 12 6C13.6667 6 15.0833 6.58333 16.25 7.75C17.4167 8.91667 18 10.3333 18 12C18 12.65 17.9 13.275 17.7 13.875C17.5 14.475 17.225 15.0167 16.875 15.5L18.3 16.925C18.8333 16.2417 19.25 15.4833 19.55 14.65C19.85 13.8167 20 12.9333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20ZM12 16C12.3667 16 12.7208 15.9542 13.0625 15.8625C13.4042 15.7708 13.725 15.6333 14.025 15.45L12.5 13.925C12.4167 13.9583 12.3333 13.9792 12.25 13.9875C12.1667 13.9958 12.0833 14 12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.1 13.9958 12.1958 13.9875 12.2875C13.9792 12.3792 13.9583 12.4667 13.925 12.55L15.425 14.05C15.6083 13.75 15.75 13.4292 15.85 13.0875C15.95 12.7458 16 12.3833 16 12C16 10.9 15.6083 9.95833 14.825 9.175C14.0417 8.39167 13.1 8 12 8C10.9 8 9.95833 8.39167 9.175 9.175C8.39167 9.95833 8 10.9 8 12C8 13.1 8.39167 14.0417 9.175 14.825C9.95833 15.6083 10.9 16 12 16Z" fill="#0574B0" />
                                                        </svg>
                                                        <span>{nearbyLocationText}</span>
                                                    </div>
                                                )}
                                                {this.variantB ? (
                                                    <SearchResultsSingleUnitCard
                                                        user={this.user}
                                                        data={{
                                                            ...facility,
                                                            showAllUnitsText: publicResources.showAllUnitsText,
                                                            showLessUnitsText: publicResources.showLessUnitsText,
                                                            makeReservationText: publicResources.makeReservationText,
                                                            checkAvailabilityText: publicResources.checkAvailabilityText,
                                                            accommodationUnitsText: publicResources.accommodationUnitsText,
                                                            numberOfUnitsText: publicResources.numberOfUnitsText,
                                                            selectText: publicResources.selectText,
                                                            cardGuestAdviceData: publicResources.cardGuestAdviceData,
                                                            showGuestAdvice: facilitiesPublicResources.showGuestAdvice,
                                                            roomsLabel: publicResources.roomsLabel,
                                                            bathroomsLabel: publicResources.bathroomsLabel,
                                                            guestsLabel: publicResources.guestsLabel,
                                                            priceTotalLabel: publicResources.priceTotalLabel,
                                                        }}
                                                        wishlistedUnits={wishlistedUnits}
                                                        updateWishlistedUnits={this.updateWishlistedUnits}
                                                        wishlistModalData={{
                                                            ...publicResources.wishlistModal,
                                                            ...publicResources.wishlistRemoveConfirmationModal,
                                                        }}
                                                        openLoginModal={() => this.setState({ isLoginModalOpen: true })}
                                                        isDateRangeSelected={selectedFilters.from && selectedFilters.to}
                                                        firstCarouselImageLazy={index !== 0}
                                                        wasFirstResult={page < 2 && index === 0 ? 1 : 0}
                                                        page={!page ? 1 : page}
                                                        intervalFilterWasUsed={intervalFilterWasUsed}
                                                        bigimg={(index !== 0) || (bigimg === 0 ? false : bigimg < index + 2)}
                                                        smallDevice={smallDevice}
                                                        trackingData={{
                                                            indexOnPage: index,
                                                            page: !page ? 1 : page,
                                                        }}
                                                        missingSearchCardAlertData={this.getMissingSearchCardAlert()}
                                                        // Potrebno dodati logiku "smallDevice ? index === 0 : index < 2" ako se ikada doda podatak o view-u da dolazi sa servera, u protivnom nema smisla.
                                                        firstCarouselImageLcpPriority={index < 2}
                                                    />
                                                ) : (
                                                    <SearchResultsCard
                                                        user={this.user}
                                                        data={{
                                                            ...facility,
                                                            showAllUnitsText: publicResources.showAllUnitsText,
                                                            showLessUnitsText: publicResources.showLessUnitsText,
                                                            makeReservationText: publicResources.makeReservationText,
                                                            checkAvailabilityText: publicResources.checkAvailabilityText,
                                                            accommodationUnitsText: publicResources.accommodationUnitsText,
                                                            numberOfUnitsText: publicResources.numberOfUnitsText,
                                                            selectText: publicResources.selectText,
                                                            cardGuestAdviceData: publicResources.cardGuestAdviceData,
                                                            showGuestAdvice: facilitiesPublicResources.showGuestAdvice,
                                                        }}
                                                        wishlistedUnits={wishlistedUnits}
                                                        updateWishlistedUnits={this.updateWishlistedUnits}
                                                        wishlistModalData={{
                                                            ...publicResources.wishlistModal,
                                                            ...publicResources.wishlistRemoveConfirmationModal,
                                                        }}
                                                        openLoginModal={() => this.setState({ isLoginModalOpen: true })}
                                                        isDateRangeSelected={selectedFilters.from && selectedFilters.to}
                                                        firstCarouselImageLazy={index !== 0}
                                                        wasFirstResult={page < 2 && index === 0 ? 1 : 0}
                                                        page={!page ? 1 : page}
                                                        intervalFilterWasUsed={intervalFilterWasUsed}
                                                        bigimg={(index !== 0) || (bigimg === 0 ? false : bigimg < index + 2)}
                                                        smallDevice={smallDevice}
                                                        trackingData={{
                                                            indexOnPage: index,
                                                            page: !page ? 1 : page,
                                                        }}
                                                        missingSearchCardAlertData={this.getMissingSearchCardAlert()}
                                                        // Potrebno dodati logiku "smallDevice ? index === 0 : index < 2" ako se ikada doda podatak o view-u da dolazi sa servera, u protivnom nema smisla.
                                                        firstCarouselImageLcpPriority={index < 2}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {facilitiesPublicResources && (
                                            <>
                                                {totalResults > 0 && (
                                                    <Pagination
                                                        currentPage={page}
                                                        totalPages={paginationData.totalPages}
                                                        pathname={paginationData.pathname}
                                                        search={paginationData.search}
                                                        data={publicResources}
                                                        trackingParams={TRACKING_PARAMS}
                                                        onPageClick={this.handlePaginationLoadMoreTracking}
                                                    />
                                                )}
                                                {totalResults === 0 && (
                                                    <div className="col-12">
                                                        <p className="searchResults--noResults">
                                                            {publicResources.zeroResultsPageText}
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {searchResultsHelpData?.suggestions?.tooFewResults && this.renderTooFewResultsBanner()}
                                        {searchResultsHelpData?.suggestions?.nearByLocations && this.renderNearbyLocationsHelpSection()}
                                    </div>
                                </div>
                                {bottomContent && (
                                    <div className="search-results-content__location-bg search-results-content__location-bg-shown">
                                        <div className={`bottom-content-left ${isBottomContentDiscountBanner ? 'discount-banner-content-left' : 'landingContentLeft'}`}>
                                            <img
                                                src={bottomContent.image.sourceURL}
                                                className="search-results-content__location-bg-img bottom-content-left-img landing-content-left-img"
                                                srcSet={
                                                    [
                                                        bottomContent.image.sourceURL && `${bottomContent.image.sourceURL} 1x`,
                                                        bottomContent.imageSourceURLMediumSizeAlternative && `${bottomContent.imageSourceURLMediumSizeAlternative} 2x`,
                                                        bottomContent.imageSourceURLLargeSizeAlternative && `${bottomContent.imageSourceURLLargeSizeAlternative} 3x`,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')
                                                }
                                                alt={bottomContent.image.altText}
                                                title={bottomContent.image.titleText}
                                                loading="lazy"
                                            />
                                            {isBottomContentDiscountBanner && <h3 className="bottom-content-left--title">{bottomContent.title}</h3>}
                                        </div>
                                        <div className="search-results-content__location-description">
                                            <div
                                                ref={this.geoLandingContentRef}
                                                className={`search-results-content__location-description-wrap ${expandTextClick ? 'search-results-content__location-description-wrap--text-open' : 'search-results-content__location-description-wrap--text-closed'}`}
                                            >
                                                <h2 className="search-results-content__location-description-wrap-h2">{bottomContent.title}</h2>
                                                <div
                                                    className="search-results-content__location-description-wrap--bottom-text-wrapp"
                                                    dangerouslySetInnerHTML={{
                                                        __html: bottomContent.text,
                                                    }}
                                                />
                                                {showExpandButton && (
                                                    <button
                                                        type="button"
                                                        className="search-results-content__location-description-wrap--expand-btn"
                                                        onClick={this.expandTextClick}
                                                    >
                                                        {expandTextClick ? `${publicResources?.showLess} ▲` : `${publicResources?.showMore} ▼`}
                                                    </button>
                                                )}
                                            </div>
                                            {bottomContent.popularLocations && (
                                                <div className="search-results-content__location-description-wrap search-results-content__popular-locations">
                                                    <h3 className="search-results-content__popular-locations-h4">
                                                        {bottomContent.popularLocationsTitle}
                                                    </h3>
                                                    <div className="search-results-content__location-description-items">
                                                        {bottomContent.popularLocations.map(
                                                            (item, index) => (
                                                                <a className="search-results-content__popular-locations-a" href={item.redirectURL} key={index} title={item.title}>
                                                                    {item.title}
                                                                </a>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {(bottomContent.accomodationTypeStatistics || bottomContent.accommodationNumber) && (
                                                <div className="accomodationTypeStatistics">
                                                    {bottomContent.accomodationTypeStatistics?.map(
                                                        (item, index) => (
                                                            <div
                                                                key={index}
                                                                className={`accomodation-type-statistics__item${accomodationStatisticsTotalCount <= 1 ? ' span-two-columns' : ''}`}
                                                            >
                                                                <a href={item.redirectURL} className="accomodation-type-statistics__item-content">
                                                                    {item.title}
                                                                    <span className="accomodation-type-statistics__item-span">{item.numberOfUnits}</span>
                                                                </a>
                                                            </div>
                                                        ),
                                                    )}
                                                    {bottomContent.accommodationNumber && (
                                                        <div className={`accomodation-type-statistics__item${accomodationStatisticsTotalCount <= 1 ? ' span-two-columns' : ''}`}>
                                                            <div className="accomodation-type-statistics__item-content">
                                                                {bottomContent.accommodationNumber.title}
                                                                <span className="accomodation-type-statistics__item-span">{bottomContent.accommodationNumber.number}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div
                                                        className={`accomodation-type-statistics__lowest-price${accomodationStatisticsTotalCount <= 1 ? ' span-two-columns' : ''}`}
                                                        dangerouslySetInnerHTML={{ __html: `
                                                        <a class="accomodation-type-statistics__lowest-price-a" href="${bottomContent.lowestPrice.redirectURL}">
                                                            ${bottomContent.lowestPrice.title}
                                                        </a>
                                                        ` }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <SearchMap
                                className="padded"
                                isOpen={mapView}
                                setIsOpen={() => this.setState((prev) => ({ mapView: !prev.mapView }))}
                                outerLoading={isLoading}
                                user={this.user}
                                data={facilitiesResultsForMap ? {
                                    results: isLoading ? null : facilitiesResultsForMap?.slice(-30),
                                    showGuestAdvice: facilitiesPublicResources.showGuestAdvice,
                                    showAllUnitsText: publicResources.showAllUnitsText,
                                    showLessUnitsText: publicResources.showLessUnitsText,
                                    makeReservationText: publicResources.makeReservationText,
                                    checkAvailabilityText: publicResources.checkAvailabilityText,
                                    accommodationUnitsText: publicResources.accommodationUnitsText,
                                    numberOfUnitsText: publicResources.numberOfUnitsText,
                                    selectText: publicResources.selectText,
                                    cardGuestAdviceData: publicResources.cardGuestAdviceData,
                                    roomsLabel: publicResources.roomsLabel,
                                    bathroomsLabel: publicResources.bathroomsLabel,
                                    guestsLabel: publicResources.guestsLabel,
                                    priceTotalLabel: publicResources.priceTotalLabel,
                                } : null}
                                wishlistedUnits={wishlistedUnits}
                                updateWishlistedUnits={this.updateWishlistedUnits}
                                wishlistModalData={{
                                    ...publicResources.wishlistModal,
                                    ...publicResources.wishlistRemoveConfirmationModal,
                                }}
                                openLoginModal={() => this.setState({ isLoginModalOpen: true })}
                                isDateRangeSelected={selectedFilters.from && selectedFilters.to}
                                update={this.debouncedupdateMapMove}
                                intervalFilterWasUsed={intervalFilterWasUsed}
                                hideFiltersSidebar={() => this.setState({ filterView: false })}
                                isFiltersSidebarOpen={filterView}
                                missingSearchCardAlertData={this.getMissingSearchCardAlert()}
                                isVariantB={this.variantB}
                            />
                            {filtersPublicResources && (
                                <FiltersModal
                                    data={{
                                        ...filtersPublicResources.form,
                                        showMoreText: publicResources.showMore,
                                        showLessText: publicResources.showLess,
                                    }}
                                    sortData={publicResources.sort}
                                    handleSort={this.handleSort}
                                    isLoading={isApplyingFilters}
                                    searchResultsCount={totalResults}
                                    handleFiltersModal={this.handleFiltersModal}
                                    handleFilterChange={this.handleFilterChange}
                                    isModalOpen={filterView}
                                    setIsModalOpen={() => this.setState((prev) => ({ filterView: !prev.filterView }))}
                                    handleApplyFilters={this.handleApplyFilters}
                                    selectedFilters={selectedFilters}
                                    selectedFiltersCount={selectedFiltersCount}
                                    handleMinChange={this.handleMinChange}
                                    handleMaxChange={this.handleMaxChange}
                                    maxPriceUpperLimit={this.maxPriceUpperLimit}
                                    handleBathrooms={this.handleBathrooms}
                                    handleCheckboxChange={this.handleCheckboxChange}
                                    handleCenterDistance={this.handleCenterDistance}
                                    handleBeachDistance={this.handleBeachDistance}
                                    handleAccommodationScore={this.handleAccommodationScore}
                                    handleResetFilters={this.handleResetFilters}
                                    handleResetSelectedFilters={this.handleResetSelectedFilters}
                                    filtersDefaults={SearchResultsContent.filtersToReset}
                                    initiallySelectedFilters={this.initiallySelectedFilters}
                                />
                            )}
                        </div>
                    </>
                )}
            </>
        );
    }
}
